"use client"

import { useEffect, useState } from "react"

export function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((val: T) => T)) => void] {
  // State to store our value
  const [storedValue, setStoredValue] = useState<T>(() => {
    // Initialize from localStorage if available (SSR-safe)
    if (typeof window === "undefined") {
      // console.log(`[useLocalStorage] SSR init for key: ${key}`)
      return initialValue
    }

    try {
      const item = window.localStorage.getItem(key)
      // console.log(`[useLocalStorage] Initial read for key: ${key}`, {
      //   found: item !== null,
      //   value: item ? JSON.parse(item) : null,
      // })
      if (item !== null) {
        return JSON.parse(item)
      }
    } catch (error) {
      console.error(`[useLocalStorage] Error reading key: ${key}`, error)
    }
    return initialValue
  })

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    try {
      const item = window.localStorage.getItem(key)
      // console.log(`[useLocalStorage] useEffect read for key: ${key}`, {
      //   found: item !== null,
      //   value: item ? JSON.parse(item) : null,
      // })
      if (item !== null) {
        setStoredValue(JSON.parse(item))
      }
    } catch (error) {
      // Swallow errors but at least surface them in devtools
      console.log(error)
    }
    // We intentionally skip initialValue here to avoid re-running on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = (value: T | ((val: T) => T)) => {
    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value

      // console.log(`[useLocalStorage] setValue called for key: ${key}`, {
      //   valueType: value instanceof Function ? "function" : "direct",
      //   currentValue: storedValue,
      //   newValue: valueToStore,
      // })

      // Save state
      setStoredValue(valueToStore)
      // Save to local storage
      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, JSON.stringify(valueToStore))
        console.log(`[useLocalStorage] Saved to localStorage for key: ${key}`, {
          value: valueToStore,
          verification: window.localStorage.getItem(key),
        })
      }
    } catch (error) {
      // A more advanced implementation would handle the error case
      console.error(
        `[useLocalStorage] Error in setValue for key: ${key}`,
        error,
      )
    }
  }

  return [storedValue, setValue]
}
